import { useRef, type ReactNode, type UIEvent } from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';

import { LIST_TABLE_ROW_HEIGHT_CLASS } from '@/components/shared/ListPagination';
import { cn } from '@/lib/utils';

import {
  getColWidth,
  headerLabelClass,
  resolveRenderHeader,
  TABLE_BODY_ROW_BORDER_CLASS,
  TABLE_HEAD_CELL_CLASS,
  TABLE_HEAD_ROW_CLASS,
} from './columnLayout';
import { TableEmptyState } from './TableEmptyState';
import { TableSpacerRows } from './TableSpacerRows';
import type { ResponsiveTableColumn } from './types';

interface ResponsiveTableStickyProps<T> {
  data: readonly T[];
  columns: ResponsiveTableColumn<T>[];
  getRowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  sort?: { column: string | null; direction: 'asc' | 'desc' | null };
  onSortChange?: (column: string) => void;
  emptyState?: ReactNode;
  ariaLabel?: string;
  className?: string;
  rowClassName?: (row: T) => string | undefined;
  hideTabletCols?: boolean;
  rowClickExcludeColumnIds?: string[];
  scrollBodyOnly?: boolean;
}

/**
 * Layout < lg: scroll orizzontale — tutte le colonne scorrono (no sticky).
 */
export function ResponsiveTableSticky<T>({
  data,
  columns,
  getRowKey,
  onRowClick,
  sort,
  onSortChange,
  emptyState,
  ariaLabel,
  className,
  rowClassName,
  hideTabletCols = true,
  rowClickExcludeColumnIds = ['selection'],
  fixedPageSize,
  scrollBodyOnly = false,
}: ResponsiveTableStickyProps<T> & { fixedPageSize?: number }) {
  const visibleCols = columns.filter((c) => !(hideTabletCols && c.hideOn === 'tablet'));
  const cellPad = 'px-3 py-2.5';
  const spacerCount = fixedPageSize ? Math.max(0, fixedPageSize - data.length) : 0;
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const bodyScrollRef = useRef<HTMLDivElement>(null);

  const syncScrollLeft = (source: HTMLDivElement, target: HTMLDivElement | null) => {
    if (target && target.scrollLeft !== source.scrollLeft) {
      target.scrollLeft = source.scrollLeft;
    }
  };

  const onBodyScroll = (e: UIEvent<HTMLDivElement>) => {
    syncScrollLeft(e.currentTarget, headerScrollRef.current);
  };

  const onHeaderScroll = (e: UIEvent<HTMLDivElement>) => {
    syncScrollLeft(e.currentTarget, bodyScrollRef.current);
  };

  if (data.length === 0) {
    return (
      <div className={cn('p-6 text-center', className)}>
        {emptyState ?? <TableEmptyState />}
      </div>
    );
  }

  const colgroup = (
    <colgroup>
      {visibleCols.map((col) => {
        const width = getColWidth(col, 'scroll');
        return (
          <col
            key={col.id}
            style={{
              width,
              minWidth: col.minWidth ?? width ?? '112px',
            }}
          />
        );
      })}
    </colgroup>
  );

  const headerTable = (
    <table className="w-max min-w-full border-collapse text-sm table-fixed">
      {colgroup}
      <thead className={TABLE_HEAD_ROW_CLASS}>
        <tr>
          {visibleCols.map((col) => (
            <ScrollTh key={col.id} col={col} sort={sort} onSortChange={onSortChange} />
          ))}
        </tr>
      </thead>
    </table>
  );

  const bodyTable = (
    <table aria-label={ariaLabel} className="w-max min-w-full text-sm table-fixed">
      {colgroup}
      <tbody>
        {data.map((row, rowIdx) => (
          <tr
            key={getRowKey(row)}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            className={cn(
              rowIdx > 0 && TABLE_BODY_ROW_BORDER_CLASS,
              'transition-colors',
              LIST_TABLE_ROW_HEIGHT_CLASS,
              onRowClick && 'cursor-pointer hover:bg-accent/50',
              rowIdx % 2 === 1 && 'bg-muted/10',
              rowClassName?.(row),
            )}
          >
            {visibleCols.map((col) => (
              <ScrollTd
                key={col.id}
                col={col}
                row={row}
                suppressRowClick={rowClickExcludeColumnIds.includes(col.id)}
              />
            ))}
          </tr>
        ))}
        <TableSpacerRows
          count={spacerCount}
          columnCount={visibleCols.length}
          cellPad={cellPad}
        />
      </tbody>
    </table>
  );

  if (scrollBodyOnly) {
    return (
      <div className={cn('flex h-full min-h-0 w-full flex-col', className)}>
        <div
          ref={headerScrollRef}
          onScroll={onHeaderScroll}
          className="shrink-0 overflow-x-auto overflow-y-hidden bg-card"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {headerTable}
        </div>
        <div
          ref={bodyScrollRef}
          onScroll={onBodyScroll}
          className="min-h-0 flex-1 overflow-auto overscroll-contain touch-pan-x"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {bodyTable}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('w-full', className)}>
      <div
        className="overflow-auto overscroll-contain touch-pan-x"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <table aria-label={ariaLabel} className="w-max min-w-full border-collapse text-sm table-fixed">
          {colgroup}
          <thead className={cn('sticky top-0 z-20', TABLE_HEAD_ROW_CLASS)}>
            <tr>
              {visibleCols.map((col) => (
                <ScrollTh key={col.id} col={col} sort={sort} onSortChange={onSortChange} />
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIdx) => (
              <tr
                key={getRowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn(
                  rowIdx > 0 && TABLE_BODY_ROW_BORDER_CLASS,
                  'transition-colors',
                  LIST_TABLE_ROW_HEIGHT_CLASS,
                  onRowClick && 'cursor-pointer hover:bg-accent/50',
                  rowIdx % 2 === 1 && 'bg-muted/10',
                  rowClassName?.(row),
                )}
              >
                {visibleCols.map((col) => (
                  <ScrollTd
                    key={col.id}
                    col={col}
                    row={row}
                    suppressRowClick={rowClickExcludeColumnIds.includes(col.id)}
                  />
                ))}
              </tr>
            ))}
            <TableSpacerRows
              count={spacerCount}
              columnCount={visibleCols.length}
              cellPad={cellPad}
            />
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ScrollTh<T>({
  col,
  sort,
  onSortChange,
}: {
  col: ResponsiveTableColumn<T>;
  sort?: { column: string | null; direction: 'asc' | 'desc' | null };
  onSortChange?: (column: string) => void;
}) {
  const align = col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left';
  const handleSortClick = col.sortable && onSortChange ? () => onSortChange(col.id) : undefined;
  const handleClick = handleSortClick ?? col.onHeaderClick;
  const isInteractive = !!handleClick;
  const isActive = sort?.column === col.id;

  return (
    <th
      className={cn(
        TABLE_HEAD_CELL_CLASS,
        'px-3 py-2.5',
        align,
        isInteractive && 'cursor-pointer select-none hover:text-foreground transition-colors',
      )}
      style={{
        minWidth: col.minWidth ?? '112px',
        width: getColWidth(col, 'scroll'),
      }}
      onClick={handleClick}
    >
      <div className={cn(
        'flex items-center gap-1',
        col.align === 'right' && 'justify-end',
        col.align === 'center' && 'justify-center',
      )}>
        {resolveRenderHeader(col) ?? (
          <span className={headerLabelClass(col.truncate)}>
            {col.label}
          </span>
        )}
        {col.sortable && !col.renderHeader && (
          isActive && sort?.direction === 'asc'
            ? <ArrowUp className="h-3 w-3 shrink-0" />
            : isActive && sort?.direction === 'desc'
              ? <ArrowDown className="h-3 w-3 shrink-0" />
              : <ArrowUpDown className="h-3 w-3 opacity-30 shrink-0" />
        )}
        {!col.sortable && col.headerPin && (
          <ArrowUpDown className="h-3 w-3 opacity-30 shrink-0" />
        )}
      </div>
    </th>
  );
}

function ScrollTd<T>({
  col,
  row,
  suppressRowClick,
}: {
  col: ResponsiveTableColumn<T>;
  row: T;
  suppressRowClick?: boolean;
}) {
  const align = col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left';
  return (
    <td
      data-row-click-suppress={suppressRowClick ? 'true' : undefined}
      className={cn(
        'px-3 py-2.5 align-middle text-[13px] leading-snug',
        align,
        col.truncate ? 'truncate overflow-hidden max-w-0' : 'whitespace-nowrap',
      )}
      style={{
        minWidth: col.minWidth ?? '112px',
        width: getColWidth(col, 'scroll'),
      }}
      onClick={suppressRowClick ? (e) => e.stopPropagation() : undefined}
    >
      {col.render(row, { isMobile: false })}
    </td>
  );
}
